import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { AppSettingService } from '@rd/core/services/app-setting/app-setting.service';
import {
  ANZ_ACCOUNT_NUMBER_VALIDATION_REGEX,
  ANZ_BSB_VALIDATION_REGEX,
  NAME_VALIDATION_REGEX,
} from '@rd/utils/regex.utils';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RedeemCashSettingService } from '../../services/redeem-cash-setting/redeem-cash-setting.service';
import { RedeemCashV2LinkAccountDetailFormData } from '../../types';

@Component({
  selector: 'rd-rcv2-link-account-detail-form',
  templateUrl: './link-account-detail-form.component.html',
  styleUrls: ['./link-account-detail-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RcV2LinkAccountDetailFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RcV2LinkAccountDetailFormComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class RcV2LinkAccountDetailFormComponent
  implements ControlValueAccessor, OnChanges, OnDestroy, OnInit, Validator
{
  @HostBinding('class.rd-rcv2-link-account-detail-form')
  componentClass = true;

  @Input() formData: RedeemCashV2LinkAccountDetailFormData;
  @Input() active: boolean;
  @Input() isSubmitted: Subject<boolean>;

  private destroy$ = new Subject();

  matFormFieldAppearance: MatFormFieldAppearance;
  accountDetailForm: UntypedFormGroup;
  propagateChange: (value: any) => {};
  onTouched: () => void = () => {};

  constructor(
    private cdRef: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
    private appSettingService: AppSettingService,
    private redeemCashSettingService: RedeemCashSettingService,
  ) {}

  ngOnInit(): void {
    this.matFormFieldAppearance = this.appSettingService.matFormFieldAppearance;

    this.accountDetailForm = this.fb.group({
      enrollmentType:
        this.redeemCashSettingService.defaultEnrollmentListCategory,
      accountFirstName: this.accountFirstNameParameters(),
      accountLastName: this.accountLastNameParameters(),
      bsbNumber: this.bsbNumberParameters(),
      accountNumber: this.accountNumberParameters(),
    });

    this.isSubmitted.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event) {
        // We want to check required validation only once the user submit
        this.setAccountDetailFormRequiredFields();
        this.accountDetailForm.markAllAsTouched();
        this.cdRef.markForCheck();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.active && this.accountDetailForm) {
      const { currentValue } = changes.active;
      if (currentValue) {
        this.accountDetailForm.enable();
      } else {
        this.accountDetailForm.disable();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  // Angular form interface methods

  writeValue(val: any): void {
    if (!!val) {
      this.accountDetailForm.setValue(val, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.accountDetailForm.valueChanges.subscribe(fn);
    this.propagateChange = fn;

    this.propagateChange(this.accountDetailForm.value);
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(c: AbstractControl): ValidationErrors | null {
    // If the form has all of its controls disabled, then the form is "invalid" also.
    // This ensures that if the WL chose to disable editing the recipient detail form, then
    // the form would always be "valid" (it's assumed that the WL would also pre-fill the form
    // if the form gets disabled)

    return this.accountDetailForm.valid
      ? null
      : { invalidAccountDetailForm: true };
  }

  // Render states

  // Validator error messages

  get isAccountFirstNameEmpty(): boolean {
    return this.validateAfterFormIsTouched(() =>
      (
        this.accountDetailForm.get('accountFirstName') as AbstractControl
      ).hasError('required'),
    );
  }

  get isAccountLastNameEmpty(): boolean {
    return this.validateAfterFormIsTouched(() =>
      (
        this.accountDetailForm.get('accountLastName') as AbstractControl
      ).hasError('required'),
    );
  }

  get isBsbNumberEmpty(): boolean {
    return this.validateAfterFormIsTouched(() =>
      (this.accountDetailForm.get('bsbNumber') as AbstractControl).hasError(
        'required',
      ),
    );
  }

  get isAccountNumberEmpty(): boolean {
    return this.validateAfterFormIsTouched(() =>
      (this.accountDetailForm.get('accountNumber') as AbstractControl).hasError(
        'required',
      ),
    );
  }

  get isAccountFirstNameInvalidPattern(): boolean {
    return this.validateAfterFormIsTouched(() =>
      (
        this.accountDetailForm.get('accountFirstName') as AbstractControl
      ).hasError('pattern'),
    );
  }

  get isAccountLastNameInvalidPattern(): boolean {
    return this.validateAfterFormIsTouched(() =>
      (
        this.accountDetailForm.get('accountLastName') as AbstractControl
      ).hasError('pattern'),
    );
  }

  get isBsbNumberInvalidPattern(): boolean {
    return this.validateAfterFormIsTouched(() =>
      (this.accountDetailForm.get('bsbNumber') as AbstractControl).hasError(
        'pattern',
      ),
    );
  }

  get isAccountNumberInvalidPattern(): boolean {
    return this.validateAfterFormIsTouched(() =>
      (this.accountDetailForm.get('accountNumber') as AbstractControl).hasError(
        'pattern',
      ),
    );
  }

  private setAccountDetailFormRequiredFields(): void {
    const { accountFirstName, accountLastName, bsbNumber, accountNumber } =
      this.accountDetailForm.controls;

    accountFirstName.setValidators([
      Validators.required,
      Validators.pattern(NAME_VALIDATION_REGEX),
    ]);
    accountFirstName.updateValueAndValidity();

    accountLastName.setValidators([
      Validators.required,
      Validators.pattern(NAME_VALIDATION_REGEX),
    ]);
    accountLastName.updateValueAndValidity();

    bsbNumber.setValidators([
      Validators.required,
      Validators.pattern(ANZ_BSB_VALIDATION_REGEX),
    ]);
    bsbNumber.updateValueAndValidity();

    accountNumber.setValidators([
      Validators.required,
      Validators.pattern(ANZ_ACCOUNT_NUMBER_VALIDATION_REGEX),
    ]);
    accountNumber.updateValueAndValidity();
  }

  // Must ensure form is touched already, as specs fail unless this check is present
  private validateAfterFormIsTouched(validateFn: () => boolean): boolean {
    return this.accountDetailForm.touched && validateFn();
  }

  // Form default parameters

  private accountFirstNameParameters(): [any, { [key: string]: any }] {
    const fieldValidators = {
      validators: [Validators.pattern(NAME_VALIDATION_REGEX)],
    };
    const prefilledData = this.formData ? this.formData.accountFirstName : '';

    return [
      {
        value: prefilledData,
        disabled: !this.active,
      },
      fieldValidators,
    ];
  }

  private accountLastNameParameters(): [any, { [key: string]: any }] {
    const fieldValidators = {
      validators: [Validators.pattern(NAME_VALIDATION_REGEX)],
    };
    const prefilledData = this.formData ? this.formData.accountLastName : '';

    return [
      {
        value: prefilledData,
        disabled: !this.active,
      },
      fieldValidators,
    ];
  }

  private bsbNumberParameters(): [any, { [key: string]: any }] {
    const fieldValidators = {
      validators: [Validators.pattern(ANZ_BSB_VALIDATION_REGEX)], // BSB number for ANZ has exact 6 digits and start with `01`
    };
    const prefilledData = this.formData ? this.formData.bsbNumber : '';

    return [
      {
        value: prefilledData,
        disabled: !this.active,
      },
      fieldValidators,
    ];
  }

  private accountNumberParameters(): [any, { [key: string]: any }] {
    const fieldValidators = {
      validators: [Validators.pattern(ANZ_ACCOUNT_NUMBER_VALIDATION_REGEX)], // ANZ Account has exact 9 digits
    };
    const prefilledData = this.formData ? this.formData.accountNumber : '';

    return [
      {
        value: prefilledData,
        disabled: !this.active,
      },
      fieldValidators,
    ];
  }
}
